import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Lock, Unlock, Edit2, ExternalLink, MessageCircle, ArrowRightLeft } from "lucide-react";
import { AssignableUser, DraggableUser } from "./DraggableUser";
import { AdminAPI } from "@shared/types/api";
import { SendMessageButton } from "./SendMessageButton";
import { RestaurantHoverCard, type PastVisit } from "./RestaurantHoverCard";

interface Table {
  id: string;
  reservationName: string;
  capacity: number;
  isLocked: boolean;
  users: AssignableUser[];
  restaurantName?: string;
  restaurantId?: string;
  restaurantAddress?: string;
  restaurantNeighborhoodName?: string;
  restaurantBookingLink?: string;
  restaurantTags?: string[];
}

interface TableCardProps {
  table: Table;
  /** City of the restaurant's neighborhood, shown in the header line. */
  cityName?: string;
  onLockToggle: (tableId: string) => void;
  onEdit: (tableId: string) => void;
  onEditUser?: (userId: string) => void;
  onToggleReview?: (userId: string) => void;
  onFlagNoShow?: (userId: string) => void;
  onReassign?: (userId: string) => void;
  /** Opens the batch move/unassign-all dialog for this table. */
  onReassignAll?: (tableId: string) => void;
  onSendEmail?: (userId: string) => void;
  onNeighborhoodClick?: (neighborhood: string) => void;
  activeUserId: string | null;
  overElementId: string | null;
  allTables: Table[];
  date: string;
  userHistory?: Record<string, AdminAPI.UserHistory>;
}

export function TableCard({
  table,
  cityName,
  onLockToggle,
  onEdit,
  onEditUser,
  onToggleReview,
  onFlagNoShow,
  onReassign,
  onReassignAll,
  onSendEmail,
  onNeighborhoodClick,
  activeUserId,
  overElementId,
  allTables,
  date,
  userHistory,
}: TableCardProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `table-${table.id}`,
  });

  // Determine if we should show hover state
  const sourceTable = allTables.find((t) =>
    t.users.some((u) => u.id === activeUserId)
  );
  const isDraggingFromElsewhere =
    activeUserId && (!sourceTable || sourceTable.id !== table.id);
  const isHoveringOverTableUser =
    overElementId && table.users.some((u) => u.id === overElementId);
  const shouldShowHover =
    isDraggingFromElsewhere && (isOver || isHoveringOverTableUser);

  return (
    <div
      ref={setNodeRef}
      className={`card transition-all duration-200 group p-4 rounded-xl ${
        shouldShowHover
          ? "ring-2 ring-primary-500 ring-offset-2 ring-offset-dark-bg bg-primary-500/5 scale-[1.02]"
          : ""
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            {table.restaurantNeighborhoodName && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNeighborhoodClick?.(table.restaurantNeighborhoodName!);
                }}
                className="block max-w-full truncate text-left text-xs text-primary-800 hover:text-primary transition-colors cursor-pointer"
                title={`Filter by ${table.restaurantNeighborhoodName}`}
              >
                {cityName ? `${cityName}: ` : ""}
                {table.restaurantNeighborhoodName}
              </button>
            )}
            <h3
              className="text-sm font-bold truncate"
              title={`${table.reservationName}${table.restaurantName ? ` - ${table.restaurantName}` : ""}`}
            >
              {table.reservationName}
              {table.restaurantName && (
                <>
                  {" - "}
                  <RestaurantHoverCard restaurantId={table.restaurantId} pastVisits={(() => {
                    if (!table.restaurantId || !userHistory) return [];
                    const visits: PastVisit[] = [];
                    for (const user of table.users) {
                      const history = userHistory[user.id];
                      if (!history) continue;
                      for (const r of history.previousRestaurants) {
                        if (r.id === table.restaurantId) {
                          visits.push({ firstName: user.name.split(" ")[0], date: r.visitedAt });
                        }
                      }
                    }
                    return visits;
                  })()}>
                    <span className="hover:text-primary transition-colors border-b border-dotted border-transparent hover:border-primary cursor-default">
                      {table.restaurantName}
                    </span>
                  </RestaurantHoverCard>
                </>
              )}
            </h3>
            {/* Display restaurant tags */}
            {table.restaurantTags && table.restaurantTags.length > 0 && (
              <div className="relative group/tags">
                <div className="flex flex-wrap gap-1 mt-1">
                  {table.restaurantTags.slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="px-1.5 py-0.5 bg-accent/10 text-accent text-[10px] rounded"
                    >
                      {tag.replace(/_/g, " ")}
                    </span>
                  ))}
                  {table.restaurantTags.length > 4 && (
                    <span className="text-[10px] text-dark-muted cursor-help">
                      +{table.restaurantTags.length - 4}
                    </span>
                  )}
                </div>
                {/* Hover tooltip showing all tags */}
                {table.restaurantTags.length > 4 && (
                  <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/tags:block">
                    <div className="bg-dark-surface border border-dark-border rounded-lg p-2 shadow-xl max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {table.restaurantTags.map((tag) => (
                          <span
                            key={tag}
                            className="px-1.5 py-0.5 bg-accent/10 text-accent text-[10px] rounded"
                          >
                            {tag.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-dark-muted">
              {table.users.length} / {table.capacity} seats
              {table.restaurantBookingLink ? (
                <>
                  &nbsp;•&nbsp;
                  <a
                    href={table.restaurantBookingLink!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-500 hover:text-primary-600 inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Book
                  </a>
                </>
              ) : (
                <>
                  &nbsp;•&nbsp;
                  <a
                    href={`https://www.opentable.com/s?dateTime=${date}T19%3A00%3A00&covers=${table.capacity}&term=${encodeURIComponent(`${table.restaurantName} ${table.restaurantNeighborhoodName}`)}&queryUnderstandingType=dateTime&showMap=false&sortBy=web_conversion`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-500"
                  >
                    OpenTable
                  </a>
                </>
              )}
              {table.reservationName && (
                <>
                  &nbsp;•&nbsp;
                  <a
                    href={`https://www.opentable.com/user/dining-dashboard?searchReservation=${encodeURIComponent(table.reservationName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-500 hover:text-primary-600 inline-flex items-center gap-1"
                    title="Open reservation on OpenTable dining dashboard"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Reservation
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap w-24">
          <a
            href={`/admin/chats/?chat=table_${table.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`btn btn-ghost btn-sm p-2 ${table.users.length === 0 ? 'text-base-content/30 pointer-events-none' : 'text-base-content/60 hover:text-primary'}`}
            title="Open table chat"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
          <SendMessageButton
            tableId={table.id}
            recipientLabel={`${table.reservationName}`}
            recipientDetails={`${table.restaurantName} (${table.users.length} user${table.users.length !== 1 ? 's' : ''})`}
            variant="icon"
            disabled={table.users.length === 0}
          />
          <button
            onClick={() => onEdit(table.id)}
            className="btn btn-ghost btn-sm p-2 text-base-content/60 hover:text-primary"
            title="Edit table"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          {onReassignAll && (
            <button
              onClick={() => onReassignAll(table.id)}
              disabled={table.users.length === 0}
              className={`btn btn-ghost btn-sm p-2 ${table.users.length === 0 ? "text-base-content/30" : "text-base-content/60 hover:text-accent"}`}
              title="Move / unassign all users on this table"
            >
              <ArrowRightLeft className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onLockToggle(table.id)}
            className={`btn btn-ghost btn-sm p-2 ${
              table.isLocked ? "text-accent" : "text-base-content/60"
            }`}
            title={table.isLocked ? "Unlock table" : "Lock table"}
          >
            {table.isLocked ? (
              <Lock className="w-4 h-4" />
            ) : (
              <Unlock className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-2 min-h-[100px]">
        <SortableContext
          items={table.users.map((u) => u.id)}
          strategy={verticalListSortingStrategy}
          disabled={table.isLocked}
        >
          {table.users.map((user) => {
            const otherMates = table.users.filter((u) => u.id !== user.id);
            const currentTableMateIds = new Set(otherMates.map((u) => u.id));
            return (
              <DraggableUser
                key={user.id}
                user={user}
                restaurantNeighborhoodName={table.restaurantNeighborhoodName}
                tableMates={otherMates}
                isAssignedToTable
                isTableLocked={table.isLocked}
                currentTableMateIds={currentTableMateIds}
                history={userHistory?.[user.id]}
                onEdit={onEditUser}
                onToggleReview={onToggleReview}
                onFlagNoShow={onFlagNoShow}
                onReassign={onReassign}
                onSendEmail={onSendEmail}
                onNeighborhoodClick={onNeighborhoodClick}
              />
            );
          })}
        </SortableContext>

        {table.users.length === 0 && (
          <div
            className={`flex items-center justify-center h-20 border-2 border-dashed rounded-lg text-sm transition-all duration-200 ${
              shouldShowHover
                ? "border-primary-500 text-primary-500 bg-primary-500/10 scale-105"
                : "border-dark-border text-dark-muted"
            }`}
          >
            {table.isLocked ? "Table locked" : "Drop users here"}
          </div>
        )}
      </div>
    </div>
  );
}

